import {useCallback, useMemo} from 'react';
import {useDispatch} from 'react-redux';

import {useMemoizedSelector, useReduxStore} from '../../../state/hooks';
import * as PanelActions from '../../../state/views/panel/actions';
import {type Ref as PanelRef} from '../../../state/views/panel/types';
import {type Ref as PanelBankConfigRef} from '../../../state/views/panelBankConfig/types';
import {
  makeSectionMediaSliderKeySelector,
  makeSectionMediaSliderSyncSelector,
} from '../../../state/views/panelBankSectionConfig/selectors';
import {type Ref as SectionRef} from '../../../state/views/panelBankSectionConfig/types';
import {
  makeWorkspaceMediaSliderKeySelector,
  makeWorkspaceMediaSliderSyncSelector,
} from '../../../state/views/workspaceSettings/selectors';
import {type Ref as WorkspaceSettingsRef} from '../../../state/views/workspaceSettings/types';
import {type Dispatch} from '../../../types/redux';
import {
  EMPTY_SECTION_SYNC_SLIDER_DATA,
  EMPTY_WORKSPACE_SYNC_SLIDER_DATA,
  makeMaybeSectionSyncSliderDataSelector,
  makeMaybeWorkspaceSyncSliderDataSelector,
  type PanelSyncSliderInfo,
} from './syncSliderStepsSelectors';

function getPanelRefsToUpdate(
  panelSyncInfo: PanelSyncSliderInfo[],
  stepSliderKey: string
): PanelRef[] | undefined {
  const panelRefs = panelSyncInfo
    .filter(
      ({viewType, panelSliderKey}) =>
        viewType === 'Media Browser' && panelSliderKey === stepSliderKey
    )
    .map(({panelRef}) => panelRef);

  return panelRefs.length > 0 ? panelRefs : undefined;
}

function syncPanelSteps(
  panelSyncInfo: PanelSyncSliderInfo[],
  sliderSync: boolean,
  newSliderValue: number,
  panelStepSliderKey: string,
  incrementedBulkSliderKey: string,
  dispatch: Dispatch
) {
  if (sliderSync && panelStepSliderKey === incrementedBulkSliderKey) {
    const panelRefsToBeUpdated = getPanelRefsToUpdate(
      panelSyncInfo,
      panelStepSliderKey
    );
    if (panelRefsToBeUpdated) {
      dispatch(
        PanelActions.updateConfigs(panelRefsToBeUpdated, {
          stepIndex: newSliderValue,
        })
      );
    }
  }
}

export const useSetSectionSyncSliderSteps = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  sectionRef: SectionRef | undefined
) => {
  const workspaceMediaSliderKey = useMemoizedSelector(
    makeWorkspaceMediaSliderKeySelector,
    workspaceSettingsRef
  );
  const sectionMediaSliderKey = useMemoizedSelector(
    makeSectionMediaSliderKeySelector,
    sectionRef
  );
  const sectionSliderSync = useMemoizedSelector(
    makeSectionMediaSliderSyncSelector,
    sectionRef
  );
  const workspaceStepSliderKey = workspaceMediaSliderKey ?? '_step';
  const sectionStepSliderKey = sectionMediaSliderKey ?? workspaceStepSliderKey;
  const sectionSliderSyncEnabled = sectionSliderSync === true;
  const store = useReduxStore();
  const sectionSyncSliderDataSelector = useMemo(
    () =>
      makeMaybeSectionSyncSliderDataSelector(sectionRef, sectionStepSliderKey),
    [sectionRef, sectionStepSliderKey]
  );
  const dispatch = useDispatch();

  return useCallback(
    (newSliderValue: number, panelStepSliderKey: string) => {
      const {panelSyncInfo} = sectionSliderSyncEnabled
        ? sectionSyncSliderDataSelector(store.getState())
        : EMPTY_SECTION_SYNC_SLIDER_DATA;

      syncPanelSteps(
        panelSyncInfo,
        sectionSliderSyncEnabled,
        newSliderValue,
        panelStepSliderKey,
        sectionStepSliderKey,
        dispatch
      );
    },
    [
      sectionSliderSyncEnabled,
      sectionStepSliderKey,
      sectionSyncSliderDataSelector,
      store,
      dispatch,
    ]
  );
};

export const useSetWorkspaceSyncSliderSteps = (
  workspaceSettingsRef: WorkspaceSettingsRef | undefined,
  panelBankConfigRef: PanelBankConfigRef | undefined
) => {
  const workspaceMediaSliderKey = useMemoizedSelector(
    makeWorkspaceMediaSliderKeySelector,
    workspaceSettingsRef
  );
  const workspaceSliderSync = useMemoizedSelector(
    makeWorkspaceMediaSliderSyncSelector,
    workspaceSettingsRef
  );
  const workspaceStepSliderKey = workspaceMediaSliderKey ?? '_step';
  const workspaceSliderSyncEnabled = workspaceSliderSync === true;
  const store = useReduxStore();
  const workspaceSyncSliderDataSelector = useMemo(
    () =>
      makeMaybeWorkspaceSyncSliderDataSelector(
        panelBankConfigRef,
        workspaceStepSliderKey,
        false
      ),
    [panelBankConfigRef, workspaceStepSliderKey]
  );
  const forceSyncSliderDataSelector = useMemo(
    () =>
      makeMaybeWorkspaceSyncSliderDataSelector(
        panelBankConfigRef,
        workspaceStepSliderKey,
        true
      ),
    [panelBankConfigRef, workspaceStepSliderKey]
  );
  const dispatch = useDispatch();

  return useCallback(
    (
      newSliderValue: number,
      incrementedPanelSliderKey: string,
      sectionSliderSync: boolean,
      forceSync: boolean
    ) => {
      const shouldDoAnything =
        forceSync || (!sectionSliderSync && workspaceSliderSyncEnabled);
      const syncSliderDataSelector = forceSync
        ? forceSyncSliderDataSelector
        : workspaceSyncSliderDataSelector;
      const {panelSyncInfo} = shouldDoAnything
        ? syncSliderDataSelector(store.getState())
        : EMPTY_WORKSPACE_SYNC_SLIDER_DATA;

      syncPanelSteps(
        panelSyncInfo,
        shouldDoAnything,
        newSliderValue,
        incrementedPanelSliderKey,
        forceSync ? incrementedPanelSliderKey : workspaceStepSliderKey,
        dispatch
      );
    },
    [
      workspaceSliderSyncEnabled,
      dispatch,
      workspaceStepSliderKey,
      workspaceSyncSliderDataSelector,
      forceSyncSliderDataSelector,
      store,
    ]
  );
};
